/**
 * Presentation helpers shared by server components. Everything renders on
 * the server with deterministic UTC output — no hydration surprises.
 */
import type { JobLocation } from "@/lib/queries";
import type { OrgType } from "@/lib/sources/types";

// NOT Intl.DateTimeFormat (CM-270). These helpers also run inside client
// components (RelatedJobsRail), so the server's string must equal the
// browser's byte for byte. Intl does not guarantee that: Node's ICU prints
// en-GB September as "Sept" while Safari prints "Sep", and every /jobs/[id]
// page whose related rail showed a September deadline threw React #418
// (hydration text mismatch) in Safari — ~5% of job pageviews by mid-September.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");

/** "15 Aug 2026" (UTC). */
export function fmtDate(d: Date): string {
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "15 Aug 2026, 23:59 UTC". */
export function fmtDateTime(d: Date): string {
  return `${fmtDate(d)}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** "just now" | "12 min ago" | "3 h ago" | "2 d ago" | absolute date. */
export function relativeTime(d: Date, now: Date = new Date()): string {
  const seconds = Math.max(0, (now.getTime() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days} d ago`;
  return fmtDate(d);
}

export type DeadlineTone = "closed" | "urgent" | "soon" | "normal";

export interface DeadlineInfo {
  tone: DeadlineTone;
  /** Compact list-row label, e.g. "Closes in 3 d". */
  short: string;
  /** Full sentence for the detail page. */
  long: string;
}

/** Urgency: <48h red ("urgent"), <7d amber ("soon"), past = closed. */
export function deadlineInfo(
  deadline: Date | null | undefined,
  now: Date = new Date(),
): DeadlineInfo | null {
  if (!deadline) return null;
  const ms = deadline.getTime() - now.getTime();
  if (ms <= 0) {
    return {
      tone: "closed",
      short: "Closed",
      long: `Closed ${fmtDate(deadline)}`,
    };
  }
  const hours = ms / 3_600_000;
  if (hours <= 48) {
    const h = Math.max(1, Math.floor(hours));
    return {
      tone: "urgent",
      short: `Closes in ${h} h`,
      long: `Closes in ${h} hour${h === 1 ? "" : "s"} — ${fmtDateTime(deadline)}`,
    };
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return {
      tone: "soon",
      short: `Closes in ${days} d`,
      long: `Closes in ${days} day${days === 1 ? "" : "s"} — ${fmtDate(deadline)}`,
    };
  }
  return {
    tone: "normal",
    short: `Closes ${fmtDate(deadline)}`,
    long: `Closes in ${days} days — ${fmtDate(deadline)}`,
  };
}

export const DEADLINE_TEXT_CLASS: Record<DeadlineTone, string> = {
  closed: "text-neutral-400",
  urgent: "text-stamp-600",
  soon: "text-amber-600",
  normal: "text-neutral-500",
};

/** Added to the index within the last 24 h. */
export function isNew(firstSeenAt: Date | null, now: Date = new Date()): boolean {
  if (!firstSeenAt) return false;
  return now.getTime() - firstSeenAt.getTime() < 24 * 3_600_000;
}

/** "Rome, Italy" — renders whatever fields the source provided. */
export function locationLabel(loc: JobLocation): string {
  return [loc.city, loc.country].filter(Boolean).join(", ");
}

/** "Rome, Italy +2 more" for compact rows. */
export function locationsSummary(
  locations: JobLocation[] | null,
  max = 1,
): string | null {
  if (!locations || locations.length === 0) return null;
  const labels = locations.map(locationLabel).filter(Boolean);
  if (labels.length === 0) return null;
  const head = labels.slice(0, max).join(" · ");
  const rest = labels.length - max;
  return rest > 0 ? `${head} +${rest} more` : head;
}

// Record<OrgType, string>, not Record<string, string>: a new entry in
// ORG_TYPES with no label here is now a compile error instead of a raw
// snake_case key silently rendered to a reader (the i18n-completeness play).
const ORG_TYPE_LABELS: Record<OrgType, string> = {
  un_agency: "UN agency",
  un_secretariat: "UN Secretariat",
  mdb: "Development bank",
  eu: "EU institution",
  igo: "Intergovernmental",
  ingo: "INGO",
  other: "Other",
  unknown: "Unclassified",
};

// `orgs.orgType` is untyped `text` in the DB, so the input can still be
// anything at runtime — the cast + fallback keeps that path safe while the
// table itself stays exhaustive against OrgType.
export function orgTypeLabel(type: string): string {
  return ORG_TYPE_LABELS[type as OrgType] ?? type;
}

/**
 * Words that don't carry identity in an org name/acronym — dropped when
 * deriving initials so e.g. "Ayuda en Acción" yields "AA" not "AEA".
 */
const BADGE_STOPWORDS = new Set([
  "the", "of", "and", "for", "en", "de", "del", "des", "la", "le", "les",
  "der", "die", "das", "und", "für", "di", "da", "e",
]);

/**
 * Badge text for the org tile — always short enough to fit the fixed-size
 * plate. Many orgs in this dataset have no real short acronym on their source
 * feed, so `acronym` is often the full name (e.g. "Danish Red Cross",
 * "Cruz Roja Venezolana"); rendering that verbatim wraps to 3 lines and bleeds
 * out of the box. Strategy:
 *   1. Already compact (no spaces, <=8 chars) — e.g. "WFP", "UK-Med" — use as-is.
 *   2. An embedded ALL-CAPS token (e.g. "ACT" in "ACT Alliance") — use that,
 *      since it's clearly the org's real abbreviation.
 *   3. Otherwise derive initials from up to 3 significant words.
 */
export function orgBadgeText(
  acronym: string | null,
  name: string | null,
  orgKey: string,
): string {
  const source = (acronym || name || orgKey).trim();
  if (!/\s/.test(source) && source.length <= 8) return source.toUpperCase();

  const words = source
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics
    .split(/[\s/]+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
    .filter(Boolean);

  const embedded = words.find((w) => /^[A-Z]{2,6}$/.test(w));
  if (embedded) return embedded.toUpperCase();

  const meaningful = words.filter((w) => !BADGE_STOPWORDS.has(w.toLowerCase()));
  const pick = (meaningful.length >= 2 ? meaningful : words).slice(0, 3);
  const initials = pick.map((w) => w[0]).join("");
  return (initials || orgKey.slice(0, 3)).toUpperCase();
}
